import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate } from 'react-router-dom';
import { ROUTE_GENERAL_EXPEND_DETAILS, ROUTE_GENERAL_EXPEND_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { getCurrentJSTDate, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { addGeneralExpend } from '../../services/GeneralExpendService';
import { getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

// ? NEW IMPORTS
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import CalendarInput from '../../share-components/CalendarInput';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';

const getLastMonthDate = () => {
    const jst = getCurrentJSTDate();
    jst.setMonth(jst.getMonth() - 1); // subtract 1 month
    return jst;
};

const AddGeneralExpend = () => {
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formValue, setFormValue] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [createdGeneralExpendId, setCreatedGeneralExpendId] = useState(null);
    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [minDate, setMinDate] = useState(getLastMonthDate);
    const navigate = useNavigate();

    const now = getCurrentJSTDate();
    const maxDate = now;
    const today = maxDate;

    const initialLoad = async () => {
        setLoading(true);
        try {
            const response = await getLastLockedYearMonth();
            setLastLockedYearMonth(response || null);
            if (response) {
                const minDate = new Date(response?.toYear, (parseInt(response?.toMonth) || 0), 1);
                setMinDate(minDate);
            }
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("GENERAL EXPEND >> NEW EXPEND");
        initialLoad();
    }, []);

    const initialValues = {
        expendedReason: '',
        expendedPrice: '',
        expendedDate: '',
        comment: ''
    };

    const validationSchema = Yup.object({
        expendedReason: Yup.string()
            .max(300, 'Expended Reason must not exceed 300 characters.')
            .required('Expended Reason is required.'),
        expendedPrice: Yup.number().nullable()
            .required('Expended Price is required.')
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Expended Price must exceed 0.')
            .max(1000000, 'Expended Price must not exceed 1000000.'),
        expendedDate: Yup.date()
            .required('Expended Date is required.')
            .max(maxDate, 'Date cannot be in the future.')
            .min(minDate, `Date cannot be before ${format(minDate, 'yyyyMMdd')}`),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.')
    });

    const onSubmit = async (form) => {
        const payload = {
            ...form,
            expendedDate: format(form.expendedDate, 'yyyyMMdd') // ? send formatted string
        };
        setFormValue(payload);
        setShowConfirmDialog(true);
    };

    const handleAddNewGeneralExpend = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const createdId = await addGeneralExpend(formValue);
            setCreatedGeneralExpendId(createdId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        if ((parseInt(createdGeneralExpendId) || 0) > 0) {
            navigate(`${ROUTE_GENERAL_EXPEND_DETAILS}/${createdGeneralExpendId}`);
        } else {
            navigate(ROUTE_GENERAL_EXPEND_LIST);
        }
    };

    return (
        <div id='addGeneralExpendPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"New Expend"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={onSubmit}>
                                        {({ setFieldValue, values }) => (
                                            <Form>
                                                {/* Expended Reason */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Reason*</label>
                                                    <Field name="expendedReason" component="textarea" rows="3" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="expendedReason" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* Expended Price */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Price*</label>
                                                    <Field
                                                        name="expendedPrice"
                                                        type="number"
                                                        inputMode="numeric"  // forces numeric keypad on mobile
                                                        pattern="[0-9]*"      // enforces only digits
                                                        className="w-100 customInputControl clearNumberSpin"
                                                        onKeyDown={(e) => {
                                                            if (["e", "-", ".", "+"].includes(e.key)) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <ErrorMessage name="expendedPrice" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* ? Expended Date as DatePicker */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Expended Date(yyyyMMdd)*</label><br />
                                                    <DatePicker
                                                        selected={values.expendedDate}
                                                        onChange={(date) => setFieldValue("expendedDate", date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        customInput={<CalendarInput isReadOnly={true} />}  // 👈 here
                                                        dayClassName={(date) =>
                                                            date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                                ? "current-day"
                                                                : undefined
                                                        }
                                                    />

                                                    {/* <DatePicker
                                                        selected={values.expendedDate}
                                                        onChange={(date) => setFieldValue('expendedDate', date)}
                                                        dateFormat="yyyy/MM/dd"
                                                        className="w-100 customInputControl"
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        placeholderText="Select date"
                                                        aria-label="Calendar date picker"
                                                        aria-haspopup="dialog"
                                                        role="combobox"
                                                        autoComplete="off"
                                                        onKeyDown={(e) => e.preventDefault()} // Stops typing 
                                                        dayClassName={(date) => {
                                                            return (
                                                                date.getDate() === today.getDate() &&
                                                                date.getMonth() === today.getMonth() &&
                                                                date.getFullYear() === today.getFullYear()
                                                            )
                                                                ? "current-day"
                                                                : undefined;
                                                        }}
                                                    /> */}
                                                    <ErrorMessage name="expendedDate" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                {/* Comment */}
                                                <div className="mb-1">
                                                    <label className='custom-label'>Comment</label>
                                                    <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                    <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                </div>

                                                <div className='floating-bottom-btn-group mt-2'>
                                                    <div className='floating-bottom-btn-grid'>
                                                        <Button
                                                            className="w-100"
                                                            type="submit"
                                                            variant="primary"
                                                            onClick={scrollToFormError}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='addGeneralExpendConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to add new expend?'
                    onConfirm={handleAddNewGeneralExpend}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your Expend has been successfully added.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default AddGeneralExpend;
